import { useState, type DragEvent } from 'react';
import IconButton from './components/IconButton';
import { sectionLabels, type LandingPageSection } from './landingPage/marketLandingPageLayout';
import { useMarketLandingPageEditor } from './hooks/useMarketLandingPageEditor';
import { useMarketRegulatoryCard } from './hooks/useMarketRegulatoryCard';
import RetailIdExpandedDesignPage from './landingPage/expandedDesign/RetailIdExpandedDesignPage';
import { getMarketCustomization, inferMarketCodeFromText } from './landingPage/CustomLanding';
import { normalizePublicLandingUrl } from './landingPage/publicLandingUrl';
import './marketLandingPageEditor.css';

function SectionRow({ section, index, count, disabled, dragged, dropEdge, onHide, onMove, onDragStart, onDragOver, onDrop, onDragEnd }: {
    section: LandingPageSection;
    index: number;
    count: number;
    disabled: boolean;
    dragged: boolean;
    dropEdge?: 'before' | 'after';
    onHide: () => void;
    onMove: (direction: -1 | 1) => void;
    onDragStart: (event: DragEvent<HTMLLIElement>) => void;
    onDragOver: (event: DragEvent<HTMLLIElement>) => void;
    onDrop: (event: DragEvent<HTMLLIElement>) => void;
    onDragEnd: () => void;
}) {
    const label = sectionLabels[section.id];
    return (
        <li className="landing-editor-row" draggable={!disabled} onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop} onDragEnd={onDragEnd}
            aria-grabbed={dragged} data-drop-edge={dropEdge} title="Drag to reorder sections">
            <span className="landing-editor-position" aria-hidden="true">{index + 1}</span>
            <span>{label}</span>
            <label className="landing-editor-hide"><input type="checkbox" role="switch" checked={section.hidden} disabled={disabled} onChange={onHide} aria-label={`Hide ${label}`} />Hide</label>
            <div className="landing-editor-move">
                <IconButton title="Move section up" ariaLabel={`Move ${label} up`} disabled={disabled || index === 0} onClick={() => onMove(-1)}><span aria-hidden="true">&#8593;</span></IconButton>
                <IconButton title="Move section down" ariaLabel={`Move ${label} down`} disabled={disabled || index === count - 1} onClick={() => onMove(1)}><span aria-hidden="true">&#8595;</span></IconButton>
            </div>
        </li>
    );
}

export default function MarketLandingPageEditor({ marketId, marketName, marketCode }: { marketId: number; marketName: string; marketCode?: string }) {
    const editor = useMarketLandingPageEditor(marketId);
    const { layout, setLayout, saved, loading, saving, error, setError, notice, setNotice, history, historyLoading, historyError, historyPage, setHistoryPage, retryLoad, retryHistory, save, restore } = editor;
    const { enabled: regulatoryEnabled, card: regulatoryCard } = useMarketRegulatoryCard(marketId);
    const [draggedKey, setDraggedKey] = useState<string | null>(null);
    const [dropLanding, setDropLanding] = useState<{ targetId: string; edge: 'before' | 'after' } | null>(null);

    if (loading) return <p role="status">Loading landing page settings...</p>;
    if (!layout) return <div role="alert"><p>{error}</p><button type="button" className="button" onClick={retryLoad}>Try again</button></div>;

    const key = layout.mode === 'Regulatory' ? 'regulatory' : 'standard';
    const sections = layout[key];
    const dirty = JSON.stringify(layout) !== JSON.stringify(saved);
    const canSave = dirty || !layout.configuredPageTypes?.includes(layout.mode);
    const regulatoryHref = normalizePublicLandingUrl(regulatoryCard?.websiteUrl) ?? undefined;
    const effectiveMarketCode = marketCode?.trim().toUpperCase() || inferMarketCodeFromText(marketName, regulatoryCard?.description);
    const customization = getMarketCustomization(effectiveMarketCode);
    const resourceLinks = (regulatoryCard?.resourceLinks ?? []).flatMap((resource) => {
        const href = normalizePublicLandingUrl(resource.url);
        return resource.label?.trim() && href ? [{ label: resource.label.trim(), href }] : [];
    });
    const resources = regulatoryCard
        ? resourceLinks.length ? resourceLinks : regulatoryHref ? [{ label: 'Regulator Website', href: regulatoryHref }] : []
        : [{ label: 'Sample product information', href: '/mock/mock-product.json' }];
    function reorder(sourceId: string, targetId: string) {
        if (saving || sourceId === targetId) return;
        setLayout((current) => {
            if (!current) return current;
            const entries = current[key];
            const from = entries.findIndex((section) => section.id === sourceId);
            const to = entries.findIndex((section) => section.id === targetId);
            if (from < 0 || to < 0) return current;
            const reordered = [...entries];
            const [moved] = reordered.splice(from, 1);
            reordered.splice(to, 0, moved);
            return {
                ...current, [key]: reordered,
                configuredPageTypes: [...new Set([...(current.configuredPageTypes ?? []), current.mode])],
            };
        });
        setNotice('');
    }
    function endDrag() {
        setDraggedKey(null);
        setDropLanding(null);
    }
    function dragSource(event: DragEvent<HTMLLIElement>) {
        const sourceKey = draggedKey || event.dataTransfer.getData('text/plain');
        const prefix = `${layout!.mode}:`;
        return sourceKey.startsWith(prefix) ? sourceKey.slice(prefix.length) : '';
    }
    function handleDragStart(event: DragEvent<HTMLLIElement>, section: LandingPageSection) {
        if (saving) { event.preventDefault(); return; }
        const sourceKey = `${layout!.mode}:${section.id}`;
        setDraggedKey(sourceKey);
        setDropLanding(null);
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', sourceKey);
    }
    function handleDragOver(event: DragEvent<HTMLLIElement>, section: LandingPageSection) {
        const from = sections.findIndex((entry) => entry.id === dragSource(event));
        const to = sections.findIndex((entry) => entry.id === section.id);
        if (saving || from < 0 || from === to) { setDropLanding(null); return; }
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setDropLanding({ targetId: section.id, edge: from < to ? 'after' : 'before' });
    }
    function handleDrop(event: DragEvent<HTMLLIElement>, section: LandingPageSection) {
        event.preventDefault();
        const sourceId = dragSource(event);
        endDrag();
        if (sourceId) reorder(sourceId, section.id);
    }
    return (
        <div className="stack landing-editor">
            <div className="landing-editor-toolbar">
                <div role="group" aria-label="Landing page type" className="landing-editor-modes">
                    {(['Standard', 'Regulatory'] as const).map((mode) => <button key={mode} type="button" aria-pressed={layout.mode === mode} disabled={saving} onClick={() => { endDrag(); setLayout({ ...layout, mode }); setHistoryPage(0); setNotice(''); }}>{mode}</button>)}
                </div>
                <div className="landing-editor-actions">
                    <button type="button" className="button" disabled={!dirty || saving} onClick={() => { setLayout(saved); setHistoryPage(0); setError(''); setNotice(''); }}>Cancel</button>
                    <button type="button" className="button cta" disabled={!canSave || saving} onClick={save}>{saving ? 'Saving...' : 'Save'}</button>
                </div>
            </div>
            {error && <p role="alert">{error}</p>}
            {notice && <p role="status">{notice}</p>}
            <div className="landing-editor-workspace">
            <div className="stack landing-editor-controls">
            <ul className="landing-editor-sections" aria-label="Required sections">
                <li className="landing-editor-row landing-editor-row--required"><span aria-hidden="true" title="Required section">&#128274;</span><span>Recall Banner</span><label className="landing-editor-hide"><input type="checkbox" role="switch" disabled checked={false} aria-label="Hide Recall Banner" />Hide</label></li>
                <li className="landing-editor-row landing-editor-row--required"><span aria-hidden="true" title="Required section">&#128274;</span><span>State Compliance Card{regulatoryEnabled === null && <span className="muted"> (MCA status unknown)</span>}</span><label className="landing-editor-hide"><input type="checkbox" role="switch" disabled checked={false} aria-label="Hide State Compliance Card" />Hide</label></li>
            </ul>
            <ul className="landing-editor-sections" aria-label={`${layout.mode} sections`}>
                {sections.map((section, index) => <SectionRow key={section.id} section={section} index={index} count={sections.length} disabled={saving}
                    dragged={draggedKey === `${layout.mode}:${section.id}`} dropEdge={dropLanding?.targetId === section.id ? dropLanding.edge : undefined}
                    onDragStart={(event) => handleDragStart(event, section)} onDragOver={(event) => handleDragOver(event, section)} onDrop={(event) => handleDrop(event, section)} onDragEnd={endDrag}
                    onMove={(direction) => { const target = sections[index + direction]; if (target) reorder(section.id, target.id); }}
                    onHide={() => { setLayout({ ...layout, [key]: sections.map((item) => item.id === section.id ? { ...item, hidden: !item.hidden } : item), configuredPageTypes: [...new Set([...(layout.configuredPageTypes ?? []), layout.mode])] }); setNotice(''); }} />)}
            </ul>
            </div>
            <aside className="landing-editor-preview" aria-label="Landing page preview">
                <h2>{marketName} · {layout.mode}</h2>
                <div className="landing-editor-preview-frame">
                    <RetailIdExpandedDesignPage
                        layout={layout}
                        productTitle="Sample Product"
                        productImageSrc="/images/evergreen-kush.svg"
                        brandText="Sample Manufacturer"
                        fullDescription="Sample product description."
                        overviewRows={[{ label: 'Market', value: marketName }, { label: 'Product type', value: 'Flower' }]}
                        labTestStatus="PASSED"
                        cannabinoids={[{ cannabinoid: 'THC', weight: '200 mg/g', perServing: '20 mg', perPackage: '700 mg', percent: '20%' }]}
                        terpenes={[{ terpene: 'Myrcene', percent: '0.5%' }]}
                        packageDetails={[['Package', 'SAMPLE-001'], ['Market', marketName]]}
                        resources={resources}
                        brandName="Sample Brand"
                        brandDescription="Sample brand information."
                        stateDescription={regulatoryCard?.description || marketName}
                        stateDescriptionFormat={regulatoryCard?.descriptionFormat}
                        licenseeNotice={customization.licenseeNotice}
                        stateWebsiteHref={regulatoryHref}
                        stateLogoSrc={regulatoryCard ? customization.stateLogoSrcOverride ?? normalizePublicLandingUrl(regulatoryCard.logoUrl) ?? undefined : undefined}
                        verifiedBadgeSrc={regulatoryCard ? customization.verifiedBadgeSrc : undefined}
                        productBrandDisclaimer={regulatoryCard && regulatoryHref && effectiveMarketCode === 'NY' ? '*Cannabis product and brand information is not provided by the Office of Cannabis Management' : undefined}
                    />
                </div>
            </aside>
            </div>
            <section className="landing-editor-history" aria-labelledby="landing-history-title" aria-busy={historyLoading}>
                <div className="landing-editor-toolbar">
                    <h2 id="landing-history-title">Historical</h2>
                    <span>{layout.mode} versions</span>
                </div>
                {historyLoading ? <p role="status">Loading historical versions...</p> : historyError ? (
                    <div role="alert"><p>{historyError}</p><button type="button" className="button" disabled={saving} onClick={retryHistory}>Retry history</button></div>
                ) : history.length ? (
                    <ul className="landing-editor-history-list" aria-label={`${layout.mode} historical versions`}>
                        {history.map((revision) => (
                            <li key={revision.id}>
                                <details>
                                    <summary>
                                        <strong>Version #{revision.id}{revision.isCurrent ? ' (Current)' : ''}</strong>
                                        <time dateTime={revision.changedAt}>{new Date(revision.changedAt).toLocaleString()}</time>
                                        <span>{revision.changedByUserEmail?.trim() || 'Unknown user'}</span>
                                    </summary>
                                    <ol className="landing-editor-history-sections">
                                        {revision.sections.map((section) => <li key={section.id}><span>{sectionLabels[section.id] ?? section.id}</span><span className="muted">{section.hidden ? 'Hidden' : 'Visible'}</span></li>)}
                                    </ol>
                                    <button type="button" className="button landing-editor-restore" disabled={saving || revision.isCurrent} onClick={() => restore(revision)} aria-label={`Restore version ${revision.id}`}>Restore</button>
                                </details>
                            </li>
                        ))}
                    </ul>
                ) : <p>No historical versions found.</p>}
                <nav className="landing-editor-history-paging" aria-label="History pages">
                    <IconButton title="Newer versions" ariaLabel="Newer versions" disabled={saving || historyLoading || historyPage === 0} onClick={() => setHistoryPage((value) => value - 1)}><span aria-hidden="true">&#8249;</span></IconButton>
                    <span>Page {historyPage + 1}</span>
                    <IconButton title="Older versions" ariaLabel="Older versions" disabled={saving || historyLoading || !!historyError || history.length < 20} onClick={() => setHistoryPage((value) => value + 1)}><span aria-hidden="true">&#8250;</span></IconButton>
                </nav>
            </section>
        </div>
    );
}